import React, { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";

import StudentIcon from "@src/lib/icons/18px/student";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@src/lib/ui/Sidebar";
import LeftNavOrgSwitcher from "@src/features/app/left-nav/LeftNavOrgSwitcher";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import { cn } from "@src/lib/utils/cn";
import { LibraryIcon } from "lucide-react";
import ChatBubbleOutlineIcon from "@src/lib/icons/18px/chat-bubble-outline";
import NewspaperOutlineIcon from "@src/lib/icons/18px/newspaper-outline";
import AdminPrimaryNavRail from "@src/features/app/left-nav/admin/AdminPrimaryNavRail";
import AdminSecondaryNavMenu from "@src/features/app/left-nav/admin/AdminSecondaryNavMenu";
import {
  AdminNavSectionId,
  findActivePrimaryItem,
  getVisiblePrimaryItems,
  getVisibleSecondaryItems,
  traineePrimaryNavItems,
} from "@src/features/app/left-nav/admin/adminNavConfig";

/** Match AdminPrimaryNavRail menu button sizing and icon treatment. */
const TRAINEE_NAV_MENU_BUTTON_CLASS = cn(
  "relative isolate h-8 gap-4 px-1.5 text-s-v4",
  "group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-1.5 group-data-[collapsible=icon]:!text-s-v4",
  "[&>svg]:!size-4 [&>svg]:translate-x-[2.5px]",
);

const TraineeLeftNav: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  const { pathname } = useRouter();
  const { open, primaryRailPinned, isMobile } = useSidebar();
  const { userActionPermissions } = useActionPermissions();
  const newMessagingEnabled = useFeatureFlag(Ff.NewMessaging);

  const visibilityContext = useMemo(
    () => ({
      permissions: userActionPermissions,
      featureFlags: {
        [Ff.NewMessaging]: newMessagingEnabled,
      },
    }),
    [userActionPermissions, newMessagingEnabled],
  );
  const visiblePrimaryItems = useMemo(
    () => getVisiblePrimaryItems(visibilityContext, traineePrimaryNavItems),
    [visibilityContext],
  );
  const routeActiveItem = useMemo(
    () => findActivePrimaryItem(pathname, visiblePrimaryItems),
    [pathname, visiblePrimaryItems],
  );
  const [selectedSectionId, setSelectedSectionId] = useState<AdminNavSectionId>(
    routeActiveItem.id,
  );

  useEffect(() => {
    setSelectedSectionId(routeActiveItem.id);
  }, [routeActiveItem.id]);

  const activeItem =
    visiblePrimaryItems.find((item) => item.id === selectedSectionId) ||
    routeActiveItem;
  const visibleSecondaryItems = useMemo(
    () => getVisibleSecondaryItems(activeItem.items, visibilityContext),
    [activeItem.items, visibilityContext],
  );
  const hasSecondaryMenu = visibleSecondaryItems.length > 0;

  const forcePrimaryRailExpanded = !hasSecondaryMenu && !compact;
  const railWidth =
    primaryRailPinned || forcePrimaryRailExpanded
      ? "var(--admin-primary-rail-expanded-width)"
      : "var(--admin-primary-rail-collapsed-width)";
  const reservedWidth = `calc(${railWidth} + ${
    hasSecondaryMenu && open ? "var(--admin-secondary-menu-width) + 8px" : "0px"
  })`;

  const flatSidebar = (
    <Sidebar>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <LeftNavOrgSwitcher />
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className={TRAINEE_NAV_MENU_BUTTON_CLASS}
                >
                  <Link href="/">
                    <StudentIcon />
                    Your assignments
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className={TRAINEE_NAV_MENU_BUTTON_CLASS}
                >
                  <Link href="/my-training/library">
                    <LibraryIcon />
                    Library
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {newMessagingEnabled && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className={TRAINEE_NAV_MENU_BUTTON_CLASS}
                  >
                    <Link href="/newsfeed">
                      <NewspaperOutlineIcon />
                      Newsfeed
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  className={TRAINEE_NAV_MENU_BUTTON_CLASS}
                >
                  <Link href="/messages">
                    <ChatBubbleOutlineIcon />
                    Messages
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );

  if (!newMessagingEnabled) {
    return compact ? null : flatSidebar;
  }

  return (
    <>
      {isMobile && flatSidebar}
      <div
        className="relative hidden h-[calc(100svh-var(--impersonation-banner-height))] shrink-0 transition-[width] duration-200 ease-out motion-reduce:transition-none md:block"
        style={{ width: reservedWidth }}
      >
        <AdminPrimaryNavRail
          items={visiblePrimaryItems}
          activeSectionId={activeItem.id}
          onSelectSection={setSelectedSectionId}
          forceExpanded={forcePrimaryRailExpanded}
          hideNewButton
          hideSettings
        />
        {hasSecondaryMenu && (
          <AdminSecondaryNavMenu
            activeItem={activeItem}
            visibilityContext={visibilityContext}
          />
        )}
      </div>
    </>
  );
};

export default TraineeLeftNav;
